import { createSelector } from "@reduxjs/toolkit";
import { HUB_ITEM } from "./hubItemFields";

/** Store type / checkbox group → stable string for diffing. */
export function normalizeStoreTypeForCompare(st) {
  if (!st) return "";
  return Array.isArray(st) ? [...st].sort().join(",") : String(st);
}

function normalizeGeocodeForCompare(raw) {
  if (!raw) return "";
  let value = raw;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return "";
    try {
      value = JSON.parse(trimmed);
    } catch {
      return trimmed;
    }
  }
  if (!value || typeof value !== "object") return "";
  const lat = Number(value.lat);
  const lon = Number(value.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return "";
  return JSON.stringify({
    lat: Number(lat.toFixed(6)),
    lon: Number(lon.toFixed(6)),
  });
}

/** Flat snapshot for Biz Details + Settings + segments (not line items). */
export function buildMetadataSnapshot(values, settings) {
  const sd = settings || {};
  return {
    name: values.name,
    storeType: normalizeStoreTypeForCompare(values.storeType),
    address: values.address,
    city: values.city,
    country: values.country,
    zip: values.zip,
    phone: values.phone,
    productSegment: values.productSegment ?? "",
    serviceSegment: values.serviceSegment ?? "",
    bizPrivate: sd.bizPrivate,
    bizOpenStatus: sd.bizOpenStatus,
    bizLive: sd.bizLive === true,
    flashEnabled: sd.flashEnabled,
    flashMessage: sd.flashMessage ?? "",
    specialDiscount:
      sd.specialDiscount === undefined || sd.specialDiscount === null
        ? ""
        : String(sd.specialDiscount),
    bizPasskeyFingerprint: sd.BizPasskeyHash ? "set" : "",
    bizImageKey: values.bizImageKey ?? "",
    mapUrl: values.mapUrl ?? "",
    geocode: normalizeGeocodeForCompare(values.geocode),
    businessDocs: JSON.stringify(sd.businessDocs || []),
    serviceCoverageScope: sd.serviceCoverageScope ?? "",
    serviceCoverageAreas: JSON.stringify(sd.serviceCoverageAreas || []),
    prebookingGroups: sd.prebookingGroups ?? "",
    socialFootprints: JSON.stringify(sd.socialFootprints || {}),
  };
}

/** Serialize one product/service row for equality (ignores _pendingImages). */
export function serializeItemForCompare(item) {
  if (!item) return "";
  const { _pendingImages, ...rest } = item;
  const images = rest[HUB_ITEM.ItemImages];
  const imgComparable = (images || [])
    .map((img) => {
      if (typeof img === "string") {
        if (img.includes("media.ziniz")) {
          const parts = img.split("/");
          return parts[parts.length - 1] || img;
        }
        return img;
      }
      const key = img?.imageKey || "";
      return key || "";
    })
    .filter(Boolean)
    .sort()
    .join("|");

  const extraComparable =
    rest.extraFields && typeof rest.extraFields === "object"
      ? JSON.stringify(
          Object.keys(rest.extraFields)
            .sort()
            .reduce((acc, k) => {
              acc[k] = rest.extraFields[k];
              return acc;
            }, {})
        )
      : "";

  const vList = rest[HUB_ITEM.Variants];
  const cList = rest[HUB_ITEM.Customisations];
  const variantsComparable =
    vList && Array.isArray(vList) ? JSON.stringify(vList) : "";

  const customisationsComparable =
    cList && Array.isArray(cList) ? JSON.stringify(cList) : "";

  const videoUrlsComparable =
    Array.isArray(rest[HUB_ITEM.VideoUrls]) ? JSON.stringify(rest[HUB_ITEM.VideoUrls]) : "";

  const stable = {
    [HUB_ITEM.ItemId]: rest[HUB_ITEM.ItemId],
    [HUB_ITEM.ItemName]: rest[HUB_ITEM.ItemName],
    [HUB_ITEM.Description]: rest[HUB_ITEM.Description],
    [HUB_ITEM.BasePrice]: rest[HUB_ITEM.BasePrice],
    [HUB_ITEM.Variants]: variantsComparable,
    [HUB_ITEM.Customisations]: customisationsComparable,
    [HUB_ITEM.Discount]: rest[HUB_ITEM.Discount],
    [HUB_ITEM.ItemType]: rest[HUB_ITEM.ItemType],
    [HUB_ITEM.ItemImages]: imgComparable,
    [HUB_ITEM.VideoUrls]: videoUrlsComparable,
    [HUB_ITEM.RequiresPrebooking]: rest[HUB_ITEM.RequiresPrebooking] === true,
    [HUB_ITEM.ItemAvailable]: rest[HUB_ITEM.ItemAvailable] !== false,
    extraFields: extraComparable,
  };
  return JSON.stringify(stable);
}

export function sortedItemsComparableKey(items) {
  if (!items?.length) return "[]";
  return JSON.stringify(
    [...items].map(serializeItemForCompare).filter(Boolean).sort()
  );
}

/**
 * When store type drops P and/or S, baseline line items for that side must be treated as
 * deleted (UI clears arrays via useEffect but backend needs explicit ids).
 */
export function mergeImplicitDeletionsFromStoreTypeChange({
  mode,
  storeTypeValues,
  deletedItemIds,
  baseline,
}) {
  const out = [...(deletedItemIds || [])];
  const dedupeKey = (e) =>
    `${e[HUB_ITEM.ItemId]}::${e[HUB_ITEM.ItemType]}`;
  const seen = new Set(out.map(dedupeKey));

  if (mode !== "edit" || !baseline?.baselineProducts || !baseline?.baselineServices) {
    return out;
  }

  const types = Array.isArray(storeTypeValues) ? storeTypeValues : [];
  const hasP = types.includes("P");
  const hasS = types.includes("S");

  if (!hasS) {
    for (const s of baseline.baselineServices) {
      const id = s?.[HUB_ITEM.ItemId];
      if (!id) continue;
      const entry = { [HUB_ITEM.ItemId]: id, [HUB_ITEM.ItemType]: "SERV" };
      const k = dedupeKey(entry);
      if (!seen.has(k)) {
        out.push(entry);
        seen.add(k);
      }
    }
  }

  if (!hasP) {
    for (const p of baseline.baselineProducts) {
      const id = p?.[HUB_ITEM.ItemId];
      if (!id) continue;
      const entry = { [HUB_ITEM.ItemId]: id, [HUB_ITEM.ItemType]: "PROD" };
      const k = dedupeKey(entry);
      if (!seen.has(k)) {
        out.push(entry);
        seen.add(k);
      }
    }
  }

  return out;
}

/**
 * Create: METADATA if no products/services; else BOTH.
 * Edit: diff vs baseline — METADATA | ITEMS | BOTH.
 */
export function computeUpdateTarget({ mode, values, BizSettings, deletedItemIds, baseline, catalogDirty }) {
  const products = values.products || [];
  const services = values.services || [];

  if (mode !== "edit") {
    const noItems = products.length === 0 && services.length === 0;
    if (noItems && !catalogDirty) return "NONE";
    return noItems ? "METADATA" : "BOTH";
  }

  if (!baseline) return "BOTH";

  const metaNow = buildMetadataSnapshot(values, BizSettings);
  const metaChanged =
    JSON.stringify(metaNow) !== JSON.stringify(baseline.metadata);

  const itemsChanged =
    (deletedItemIds && deletedItemIds.length > 0) ||
    sortedItemsComparableKey(products) !== baseline.productsKey ||
    sortedItemsComparableKey(services) !== baseline.servicesKey;

  if (metaChanged && itemsChanged) return "BOTH";
  if (metaChanged) return "METADATA";
  if (itemsChanged) return "ITEMS";
  return "NONE";
}

export const selectBizEditBaseline = (state) => state.bizEdit?.baseline ?? null;

/**
 * Params: { mode, values, BizSettings, deletedItemIds }
 */
export const selectUpdateTarget = createSelector(
  [
    selectBizEditBaseline,
    (_, params) => params?.mode,
    (_, params) => params?.values,
    (_, params) => params?.BizSettings,
    (_, params) => params?.deletedItemIds,
  ],
  (baseline, mode, values, BizSettings, deletedItemIds) =>
    computeUpdateTarget({
      mode,
      values,
      BizSettings,
      deletedItemIds,
      baseline,
    })
);
